import { useEffect, useMemo } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { DataSet } from "vis-data";
import { TimelineGroup, TimelineItem } from "vis-timeline/standalone";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { playbackActions, playbackThunks } from "./store/slice";
import { playbackSessionSelectors } from "./store/selectors.session";
import { selectDeviceByIdFactory } from "features/devices/store/selectors";

import { unwrappableActionSnackbarNotificationDecorator } from "utils/unwrappableActionSnackbarNotificationDecorator";
import { FetchRecordingsForDeviceRejectedReason } from "./types/FetchRecordingsForDeviceRejectedReason";

export interface PlaybackTimelineGroupProps {
    timelineGroups: React.MutableRefObject<DataSet<TimelineGroup, "id">>;
    timelineItems: React.MutableRefObject<DataSet<TimelineItem, "id">>;
    deviceId: EntityId;
    startDateTime: string;
    endDateTime: string;
}

export function PlaybackTimelineGroup(props: PlaybackTimelineGroupProps) {
    const dispatch = useAppDispatch();

    // device
    const selectDeviceById = useMemo(() => selectDeviceByIdFactory(), []);
    const device = useAppSelector((state: RootState) =>
        selectDeviceById(state, props.deviceId.toString()),
    );

    // fetch recordings for this device
    useEffect(() => {
        const fetchRecordingsForDevice = dispatch(
            playbackThunks.fetchRecordingsForDeviceAsync({
                deviceId: props.deviceId,
                startDateTime: props.startDateTime,
                endDateTime: props.endDateTime,
            }),
        );

        unwrappableActionSnackbarNotificationDecorator(
            fetchRecordingsForDevice,
            undefined,
            (reason: FetchRecordingsForDeviceRejectedReason) => {
                // deselect device
                dispatch(
                    playbackActions.upsertDeviceSelectionState({
                        deviceId: props.deviceId,
                        selected: false,
                    }),
                );

                return reason.message;
            },
        );

        return () => fetchRecordingsForDevice?.abort();
    }, [dispatch, props.deviceId, props.endDateTime, props.startDateTime]);

    // get recordings for this device from store
    const selectRecordingsByDeviceId = useMemo(
        () => playbackSessionSelectors.selectRecordingsByDeviceIdFactory(),
        [],
    );

    const deviceRecordings = useAppSelector((state: RootState) =>
        selectRecordingsByDeviceId(state, props.deviceId),
    );

    // draw recordings on timeline
    useEffect(() => {
        if (
            !props.timelineGroups.current ||
            !props.timelineItems.current ||
            !device?.name
        )
            return;

        // upsert group
        const groupId = props.deviceId.toString();
        const existingGroup = props.timelineGroups.current.get(groupId);

        if (!existingGroup) {
            props.timelineGroups.current.add({
                id: groupId,
                content: device?.name,
            });
        }

        // upsert recordings
        props.timelineItems.current.remove(deviceRecordings.map((x) => x.id));
        const items = deviceRecordings.map<TimelineItem>((recording) => ({
            id: recording.id,
            group: groupId,
            start: recording.startTime,
            end: recording.stopTime,
            type: "range",
            content: "",
            title: recording.id.toString(),
            editable: false,
        }));

        props.timelineItems.current.add(items);

        // remove group and items when user deselects device
        return () => {
            props.timelineGroups.current?.remove(groupId);
            props.timelineItems.current?.remove(
                deviceRecordings.map((x) => x.id),
            );
        };
    }, [
        deviceRecordings,
        device?.name,
        props.deviceId,
        props.timelineGroups,
        props.timelineItems,
        device,
    ]);

    // in the future create a React based timeline
    return <></>;
}
